// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
export function formatDataRate(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return '—';
  if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(2)} Gbps`;
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(1)} kbps`;
  return `${Math.round(bps)} bps`;
}

export function formatPower(dbm: number): string {
  if (!Number.isFinite(dbm) || dbm === 0) return '—';
  return `${dbm.toFixed(0)} dBm`;
}

export function formatRTLT(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)} ms`;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function formatDistance(km: number): string {
  if (!Number.isFinite(km) || km < 0) return '—';
  if (km >= 1_000_000_000) return `${(km / 1_000_000_000).toFixed(2)} billion km`;
  if (km >= 1_000_000) return `${(km / 1_000_000).toFixed(2)} million km`;
  if (km >= 1_000) return `${(km / 1_000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} thousand km`;
  return `${km.toFixed(0)} km`;
}

export function formatAU(km: number): string {
  if (!Number.isFinite(km) || km < 0) return '';
  const AU_KM = 149_597_871;
  const au = km / AU_KM;
  if (au < 0.01) return '';
  if (au < 1) return `${au.toFixed(2)} AU`;
  return `${au.toFixed(1)} AU`;
}

export function formatNumber(n: number, decimals = 0): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatClockUTC(date: Date): string {
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mm = String(date.getUTCMinutes()).padStart(2, '0');
  const ss = String(date.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

export function formatRelative(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export function formatAzEl(azimuth: number | null, elevation: number | null): string {
  if (azimuth === null || elevation === null) return '—';
  return `${Math.round(azimuth)}° / ${Math.round(elevation)}°`;
}

export function compassBearing(azimuth: number | null): string {
  if (azimuth === null) return '—';
  const labels = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(azimuth / 45) % 8;
  return labels[idx];
}
